// Ingredient cards (Custom- Ingredients). The browser scrolls the cards; this moves the progress line, pages the
// desktop arrows, lets a mouse drag the row, lines up the names, and opens one "How it works" panel at a time.
(() => {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const scroller = root.querySelector('[data-scroller]');
    if (!scroller) return;
    const cards = [...root.querySelectorAll('[data-card]')];
    const thumb = root.querySelector('[data-thumb]');
    const previous = root.querySelector('[data-previous]');
    const next = root.querySelector('[data-next]');
    const step = () => (cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : scroller.clientWidth);
    const smooth = () => (reduceMotion.matches ? 'auto' : 'smooth');

    // Progress line and arrow states. The thumb's width changes only on resize; scrolling moves it with a transform.
    let queued = false;
    let visible = 1;
    function measure() {
      visible = Math.min(1, scroller.clientWidth / scroller.scrollWidth);
      thumb.style.width = `${visible * 100}%`;
      update();
    }
    function update() {
      queued = false;
      const max = scroller.scrollWidth - scroller.clientWidth;
      const x = scroller.scrollLeft;
      thumb.style.transform = `translateX(${(max > 0 ? x / max : 0) * (1 / visible - 1) * 100}%)`;
      if (previous) previous.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
    }
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };
    scroller.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', measure);
    measure();

    // Titles: every card's title is as tall as the tallest name, so names line up on their last line.
    const titles = [...root.querySelectorAll('[data-title]')];
    const alignTitles = () => {
      const tallest = Math.max(0, ...titles.map((title) => title.offsetHeight));
      root.style.setProperty('--ingredient-title-height', `${tallest}px`);
    };
    new ResizeObserver(alignTitles).observe(scroller);
    document.fonts?.ready.then(alignTitles);

    // Arrows move a screenful of cards and line the next one up with the left edge.
    let glide = 0;
    const page = (direction) => {
      cancelAnimationFrame(glide);
      const size = step();
      const perView = Math.max(1, Math.floor(scroller.clientWidth / size - 0.5));
      const target = Math.round(scroller.scrollLeft / size + direction * perView) * size;
      scroller.scrollTo({ left: target, behavior: smooth() });
    };
    previous?.addEventListener('click', () => page(-1));
    next?.addEventListener('click', () => page(1));

    // Mouse drag: the row follows the mouse, then glides on and slows to a stop, like a trackpad or phone swipe.
    let drag = null;
    let dragged = false;
    scroller.addEventListener('pointerdown', (event) => {
      cancelAnimationFrame(glide);
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: scroller.scrollLeft, id: event.pointerId, moving: false, samples: [] };
      dragged = false;
    });
    scroller.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moving) {
        if (Math.abs(dx) < 6) return;
        drag.moving = true;
        scroller.setPointerCapture(drag.id);
        scroller.classList.add('is-dragging');
        window.getSelection()?.removeAllRanges();
      }
      scroller.scrollLeft = drag.left - dx;
      const now = performance.now();
      drag.samples.push({ t: now, x: event.clientX });
      while (drag.samples.length > 2 && drag.samples[0].t < now - 100) drag.samples.shift();
    });
    const endDrag = () => {
      if (!drag) return;
      const { moving, samples } = drag;
      drag = null;
      if (!moving) return;
      dragged = true;
      scroller.classList.remove('is-dragging');
      if (reduceMotion.matches || samples.length < 2) return;
      const first = samples[0];
      const last = samples[samples.length - 1];
      // px per ms; no glide if the mouse paused before letting go
      let velocity = performance.now() - last.t > 80 ? 0 : -(last.x - first.x) / Math.max(1, last.t - first.t);
      let time = performance.now();
      const tick = (now) => {
        const elapsed = now - time;
        time = now;
        scroller.scrollLeft += velocity * elapsed;
        velocity *= Math.pow(0.95, elapsed / 16);
        if (Math.abs(velocity) > 0.02) glide = requestAnimationFrame(tick);
      };
      if (Math.abs(velocity) > 0.1) glide = requestAnimationFrame(tick);
    };
    scroller.addEventListener('pointerup', endDrag);
    scroller.addEventListener('pointercancel', endDrag);
    scroller.addEventListener('wheel', () => cancelAnimationFrame(glide), { passive: true });
    scroller.addEventListener('click', (event) => {
      if (!dragged) return;
      dragged = false;
      event.preventDefault();
      event.stopPropagation();
    }, true);
    scroller.addEventListener('dragstart', (event) => event.preventDefault());

    // "How it works": one open at a time; it closes when its card leaves the row's view.
    let openCard = null;
    function setOpen(card, open) {
      const toggle = card.querySelector('[data-toggle]');
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      const run = (panel.run || 0) + 1;
      panel.run = run;
      toggle.setAttribute('aria-expanded', String(open));
      const finish = (callback) => {
        const done = () => { if (panel.run === run) callback(); };
        if (parseFloat(getComputedStyle(panel).transitionDuration) === 0) return done();
        panel.addEventListener('transitionend', function end(event) {
          if (event.target !== panel || event.propertyName !== 'height') return;
          panel.removeEventListener('transitionend', end);
          done();
        });
      };
      if (open) {
        card.classList.add('is-open');
        const from = panel.hidden ? 0 : panel.offsetHeight;
        panel.hidden = false;
        panel.style.height = 'auto';
        const to = panel.offsetHeight;
        panel.style.height = `${from}px`;
        panel.offsetHeight; // start the transition from the current height
        panel.classList.add('is-open');
        panel.style.height = `${to}px`;
        finish(() => { panel.style.height = ''; });
        openCard = card;
        visibility.observe(card);
      } else {
        panel.style.height = `${panel.offsetHeight}px`;
        panel.offsetHeight;
        panel.classList.remove('is-open');
        panel.style.height = '0px';
        finish(() => {
          panel.hidden = true;
          panel.style.height = '';
          card.classList.remove('is-open');
        });
        visibility.unobserve(card);
        if (openCard === card) openCard = null;
      }
    }
    const visibility = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.target === openCard) setOpen(entry.target, false);
      });
    }, { root: scroller });

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-toggle]');
      if (!toggle) return;
      const card = toggle.closest('[data-card]');
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      if (open && openCard && openCard !== card) setOpen(openCard, false);
      setOpen(card, open);
    });
  }

  const start = () => document.querySelectorAll('[data-ingredient-cards]').forEach(init);
  start();
  document.addEventListener('shopify:section:load', start);
})();
